import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Bell, Brain } from 'lucide-react';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try { const r = await api.get('/dashboard'); setData(r.data); } catch (e) {}
    setLoading(false);
  };

  const fc = (v) => 'TZS ' + Number(v || 0).toLocaleString('en');
  const greet = () => { const h = new Date().getHours(); return h < 12 ? 'Habari za asubuhi' : h < 16 ? 'Habari za mchana' : 'Habari za jioni'; };
  const netCF = (Number(data?.revenue) || 0) - (Number(data?.expenses) || 0);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  const unreadCount = data?.unreadInsights || 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-xs">{greet()}</p>
          <h2 className="text-white text-xl font-bold">{user?.shopName || 'Shop'} 👋</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* AI Insights Bell */}
          <button onClick={() => navigate('/ai-insights')} className="relative p-2 bg-[#0d1410] rounded-lg border border-white/5 hover:border-emerald-500/30 transition">
            <Bell size={18} className="text-gray-400" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>
          <div className="flex bg-[#0d1410] rounded-lg p-0.5 gap-0.5">
            {['Day','Week','Month'].map(p => (
              <button key={p} onClick={() => setPeriod(p.toLowerCase())}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${period === p.toLowerCase() ? 'bg-emerald-600 text-white' : 'text-gray-500'}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      {/* AI Insights Alert Banner */}
      {unreadCount > 0 && (
        <button onClick={() => navigate('/ai-insights')} 
          className="w-full bg-purple-900/30 border border-purple-500/30 rounded-xl p-3 flex items-center gap-3 hover:bg-purple-900/50 transition">
          <Brain size={20} className="text-purple-400" />
          <div className="text-left">
            <p className="text-purple-300 text-sm font-medium">You have {unreadCount} new AI insight{unreadCount > 1 ? 's' : ''}</p>
            <p className="text-purple-400/60 text-xs">Tap to view</p>
          </div>
        </button>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Revenue', value: fc(data?.revenue), color: '#10b981' },
          { label: 'Profit', value: fc(data?.profit), color: '#3b82f6' },
          { label: 'Expenses', value: fc(data?.expenses), color: '#f97316' },
          { label: 'Cash Flow', value: fc(netCF), color: '#06b6d4' },
        ].map(s => (
          <div key={s.label} className="bg-[#0d1410] rounded-xl p-4 border border-white/5">
            <div className="w-2 h-2 rounded-full mb-3" style={{backgroundColor: s.color}}></div>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">{s.label}</p>
            <p className="text-white text-lg font-bold mt-1 truncate">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-white text-sm font-semibold mb-3">Quick Actions</h3>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Sale', emoji: '🛒', path: '/sales' },
            { label: 'Stock', emoji: '📦', path: '/products' },
            { label: 'Expense', emoji: '📋', path: '/expenses' },
            { label: 'Loan', emoji: '🏦', path: '/loans' },
          ].map(a => (
            <button key={a.label} onClick={() => navigate(a.path)}
              className="bg-[#0d1410] rounded-xl p-3 border border-white/5 flex flex-col items-center gap-1.5 hover:border-emerald-500/30 transition">
              <span className="text-xl">{a.emoji}</span>
              <span className="text-white text-[11px] font-medium">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-white text-sm font-semibold mb-3">Recent Activity</h3>
        <div className="bg-[#0d1410] rounded-xl border border-white/5 p-4">
          {(!data?.recentActivity || data.recentActivity.length === 0) ? (
            <div className="text-center py-6">
              <p className="text-3xl mb-2">📭</p>
              <p className="text-gray-500 text-sm">{t('no_data')}</p>
              <button onClick={() => navigate('/sales')} className="mt-3 text-emerald-400 text-xs font-medium hover:underline">
                Record your first sale →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {data.recentActivity.slice(0, 6).map((a, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-lg">{a.type === 'sale' ? '🛒' : a.type === 'expense' ? '📋' : '📦'}</span>
                    <div className="min-w-0">
                      <p className="text-gray-300 text-xs truncate">{a.description}</p>
                      <p className="text-gray-600 text-[10px]">{a.date}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium ml-2 ${a.type === 'expense' ? 'text-red-400' : 'text-emerald-400'}`}>
                    {a.type === 'expense' ? '-' : '+'}{fc(a.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
